import React, { useEffect, useState } from "react";
import { NavLink, Outlet, Link, useLocation, useNavigate, Navigate } from "react-router-dom";
import { LayoutDashboard, BarChart3, Package, Store, LogOut, Cpu, Grid3x3, FileBarChart, ScanLine, Warehouse as WarehouseIcon, Users, FileSpreadsheet, Download, Smartphone, Palette, Award, RotateCcw, Star, CreditCard, Hotel, Dog, ChevronDown, PieChart, ReceiptText, ShieldAlert, IceCream, Settings2, Info, Bell, Scissors, Utensils, Mail } from "lucide-react";
import { useAuth } from "@/lib/auth";
import { api, hasPerm } from "@/lib/api";
import { useFeatures } from "@/lib/features";
import CheckUpdateButton from "@/components/CheckUpdateButton";
import StoreSwitcher from "@/components/StoreSwitcher";
import ErrorBoundary from "@/components/ErrorBoundary";
import { isVeltaApk, staffHomePath, staffCanOpenDashboardPath } from "@/lib/staffAccess";
import { isLodgingOnly } from "@/lib/activityCatalog";

const CORE_MENU = ["home", "messages", "sales", "products", "printers", "branding", "staff", "closures", "documents", "billing", "cassa", "sistema", "stores"];
const RETAIL_EXTRAS = ["stats", "report-ops", "warehouse", "stores", "invoicing", "exports", "gestionale", "refunds", "loyalty"];
const RESTO_EXTRAS = ["stats", "tables", "palmare", "report-ops", "warehouse", "stores", "invoicing", "exports", "gestionale", "refunds", "loyalty"];
const MODE_MENU_EXTRAS = {
  negozio_animali: [...RETAIL_EXTRAS, "appointments", "reminder", "rep-toel"],
  toelettatura: ["appointments", "reminder", "rep-toel", "warehouse", "stores", "stats", "loyalty"],
  negozio: RETAIL_EXTRAS, alimentari: RETAIL_EXTRAS, macelleria: RETAIL_EXTRAS, panetteria: RETAIL_EXTRAS,
  tabacchi: RETAIL_EXTRAS, edicola: RETAIL_EXTRAS, enoteca: RETAIL_EXTRAS,
  parrucchiere: ["stats", "appointments-beauty", "loyalty", "reminder"],
  lavanderia: ["stats", "loyalty"],
  autolavaggio: ["stats", "loyalty"],
  ristorante: [...RESTO_EXTRAS, "reservations"], bar: [...RESTO_EXTRAS, "reservations"], pizzeria: [...RESTO_EXTRAS, "reservations"],
  gelateria: [...RESTO_EXTRAS, "gelateria-config"],
  festa: ["stats", "palmare", "gestionale", "refunds"],
  bnb: ["stats", "hotel", "hotel-clients", "invoicing", "gestionale"],
  alloggio_dedicato: ["stats", "hotel-clients", "invoicing", "gestionale"],
};

const Item = ({ to, icon: Icon, children, testid, perm, feature, badge }) => {
  const { hasFeature } = useFeatures();
  const location = useLocation();
  if (perm && !(Array.isArray(perm) ? perm.some(hasPerm) : hasPerm(perm))) return null;
  if (feature && !hasFeature(feature)) return null;
  const [path, search] = String(to).split("?");
  return (
    <NavLink
      to={to} end={to === "/dashboard"}
      data-testid={testid}
      title={typeof children === "string" ? children : undefined}
      className={({ isActive }) => {
        let on = isActive;
        if (search) on = location.pathname === path && location.search.includes(search);
        else if (path === "/dashboard/alberghiero") on = location.pathname === path && !location.search.includes("tab=guests");
        return `flex items-center gap-3 px-3 h-11 rounded-xl text-sm font-medium transition-colors ${
          on ? "bg-[#18181B] text-white" : "text-[#3F3F46] hover:bg-[#F4F4F5]"
        }`;
      }}
    >
      <Icon className="w-5 h-5 shrink-0" strokeWidth={1.8} />
      <span className="truncate">{children}</span>
      {Number(badge) > 0 && (
        <span className="ml-auto min-w-[1.25rem] h-5 px-1 rounded-full bg-[#B73C27] text-white text-[11px] font-semibold leading-5 text-center">
          {badge > 99 ? "99+" : badge}
        </span>
      )}
    </NavLink>
  );
};

const SubItem = ({ to, icon: Icon, children, testid }) => (
  <NavLink
    to={to}
    data-testid={testid}
    className={({ isActive }) =>
      `flex items-center gap-2.5 px-3 h-10 rounded-lg text-[13px] font-medium transition-colors ${
        isActive ? "bg-[#18181B] text-white" : "text-[#71717A] hover:text-[#18181B] hover:bg-[#F4F4F5]"
      }`
    }
  >
    <Icon className="w-4 h-4" strokeWidth={1.8} />
    {children}
  </NavLink>
);

const STATS_ROUTES = ["/dashboard/statistiche", "/dashboard/vendite", "/dashboard/documenti", "/dashboard/movimenti-gestionali"];
const REPORT_PREFIXES = [...STATS_ROUTES, "/dashboard/report-operatori", "/dashboard/storni", "/dashboard/report-toelettatura"];
const SETTINGS_PREFIXES = [
  "/dashboard/punti-vendita", "/dashboard/stampanti", "/dashboard/fatturazione",
  "/dashboard/esportazioni", "/dashboard/branding", "/dashboard/utenti",
  "/dashboard/abbonamento", "/dashboard/gelateria", "/dashboard/sistema",
];

// Nessuna chiamata API/axios rilevata qui che serva essere wrappata in try/catch

export default function DashboardLayout() {
  const { user, logout, exitImpersonation } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const isOwner = user?.role === "client_owner" || user?.role === "client" || user?.is_demo;
  const [statsOpen, setStatsOpen] = useState(STATS_ROUTES.some((r) => location.pathname.startsWith(r)));
  const [reportsOpen, setReportsOpen] = useState(REPORT_PREFIXES.some((r) => location.pathname.startsWith(r)));
  const [settingsOpen, setSettingsOpen] = useState(SETTINGS_PREFIXES.some((r) => location.pathname.startsWith(r)));
  const [unreadMsgs, setUnreadMsgs] = useState(0);
  const { modes: tenantModes, hasFeature } = useFeatures();
  const knownModes = (Array.isArray(tenantModes) ? tenantModes : []).filter((m) => MODE_MENU_EXTRAS[m]);
  const onlyLodging = isLodgingOnly(knownModes);
  const dedicatedLodging = onlyLodging && knownModes.includes("alloggio_dedicato") && !knownModes.includes("bnb");
  const menuSet = new Set([
    ...CORE_MENU,
    ...[].concat(...(Array.isArray(knownModes) ? knownModes : []).map((m) => MODE_MENU_EXTRAS[m] || [])),
  ]);
  const show = (key) => {
    if (onlyLodging && key === "products") return false;
    return menuSet.has(key);
  };
  const canStats = hasPerm("statistiche") && (show("stats") || show("sales"));
  const canDocs = hasPerm("chiusura_fiscale") && show("documents");
  const canGest = isOwner && show("gestionale");
  const statsGroupVisible = canStats || canDocs || canGest;
  const permOk = (p) => !p || (Array.isArray(p) ? p.some(hasPerm) : hasPerm(p));
  const vis = (it) => {
    const listed = show(it.key) || (it.key === "reminder" && hasFeature("whatsapp_reminders"));
    return listed && permOk(it.perm) && (!it.feature || hasFeature(it.feature)) && (!it.ownerOnly || isOwner);
  };

  const dailyItems = [
    { key: "products", to: "/dashboard/articoli", icon: Package, testid: "dash-nav-products", perm: "gestionale", label: "Articoli" },
    { key: "warehouse", to: "/dashboard/magazzino", icon: WarehouseIcon, testid: "dash-nav-warehouse", perm: "magazzino", feature: "warehouse", label: "Magazzino" },
    { key: "tables", to: "/dashboard/tavoli", icon: Grid3x3, testid: "dash-nav-tables", perm: "gestionale", label: "Tavoli" },
    { key: "reservations", to: "/dashboard/prenotazioni", icon: Utensils, testid: "dash-nav-reservations", perm: "gestionale", feature: "restaurant_reservations", label: "Prenotazioni" },
    { key: "appointments", to: "/dashboard/appuntamenti", icon: Dog, testid: "dash-nav-appointments", perm: "appuntamenti", feature: "appointments", label: "Appuntamenti" },
    { key: "appointments-beauty", to: "/dashboard/appuntamenti-estetica", icon: Scissors, testid: "dash-nav-appointments-beauty", perm: "appuntamenti", feature: "appointments", label: "Estetica" },
    { key: "reminder", to: "/dashboard/reminder", icon: Bell, testid: "dash-nav-reminder", perm: "appuntamenti", feature: "whatsapp_reminders", label: "Reminder" },
    { key: "loyalty", to: "/dashboard/fedelta", icon: Star, testid: "dash-nav-loyalty", perm: "gestionale", label: "Clienti" },
    { key: "hotel", to: "/dashboard/alberghiero", icon: Hotel, testid: "dash-nav-hotel", perm: "gestionale", feature: "hotel_module", label: "Alberghiero" },
    { key: "hotel-clients", to: "/dashboard/alberghiero?tab=guests", icon: Users, testid: "dash-nav-hotel-clients", perm: "gestionale", feature: "hotel_module", label: onlyLodging ? "Clienti" : "Clienti alloggio" },
    { key: "closures", to: "/dashboard/chiusure", icon: FileBarChart, testid: "dash-nav-closures", perm: "statistiche", label: "Chiusure" },
  ];
  const reportItems = [
    { key: "report-ops", to: "/dashboard/report-operatori", icon: Award, testid: "dash-nav-report-ops", perm: "statistiche", feature: "operator_reports", label: "Operatori" },
    { key: "refunds", to: "/dashboard/storni", icon: RotateCcw, testid: "dash-nav-refunds", perm: "chiusura_fiscale", feature: "refunds", label: "Storni / Resi" },
    { key: "rep-toel", to: "/dashboard/report-toelettatura", icon: FileBarChart, testid: "dash-nav-rep-toel", perm: "stats", label: "Toelettatura" },
  ];
  const settingsItems = [
    { key: "gelateria-config", to: "/dashboard/gelateria", icon: IceCream, testid: "dash-nav-gelateria", perm: "gestionale", feature: "gelateria", label: "Gelateria" },
    { key: "stores", to: "/dashboard/punti-vendita", icon: Store, testid: "dash-nav-stores", perm: "gestionale", ownerOnly: true, label: "Punti vendita" },
    { key: "printers", to: "/dashboard/stampanti", icon: Cpu, testid: "dash-nav-printers", perm: "stampanti", label: "Stampanti" },
    { key: "invoicing", to: "/dashboard/fatturazione", icon: FileSpreadsheet, testid: "dash-nav-invoicing", perm: "fatturazione", feature: "invoicing_manual", label: "Fatturazione" },
    { key: "exports", to: "/dashboard/esportazioni", icon: Download, testid: "dash-nav-exports", perm: "esportazioni", feature: "erp_export", label: "Esportazioni ERP" },
    { key: "branding", to: "/dashboard/branding", icon: Palette, testid: "dash-nav-branding", perm: "branding", label: "Branding" },
    { key: "staff", to: "/dashboard/utenti", icon: Users, testid: "dash-nav-staff", perm: "utenti", ownerOnly: true, label: "Utenti" },
    { key: "billing", to: "/dashboard/abbonamento", icon: CreditCard, testid: "dash-nav-billing", perm: "gestionale", ownerOnly: true, label: "Contratto" },
    { key: "sistema", to: "/dashboard/sistema", icon: Info, testid: "dash-nav-sistema", ownerOnly: true, label: "Informazioni sistema" },
  ];

  useEffect(() => {
    let cancel = false;
    const loadUnread = () => {
      api.get("/messages").then((r) => {
        if (cancel) return;
        const rows = Array.isArray(r.data) ? r.data : [];
        setUnreadMsgs(rows.filter((m) => !m.read_at).length);
      }).catch(() => {});
    };
    loadUnread();
    window.addEventListener("velta-messages-changed", loadUnread);
    return () => {
      cancel = true;
      window.removeEventListener("velta-messages-changed", loadUnread);
    };
  }, [location.pathname]);

  useEffect(() => {
    if (REPORT_PREFIXES.some((r) => location.pathname.startsWith(r))) {
      setReportsOpen(true);
      if (STATS_ROUTES.some((r) => location.pathname.startsWith(r))) setStatsOpen(true);
    }
    if (SETTINGS_PREFIXES.some((r) => location.pathname.startsWith(r))) setSettingsOpen(true);
  }, [location.pathname]);

  const toggleStats = () => {
    if (statsOpen) {
      setStatsOpen(false);
    } else {
      setStatsOpen(true);
      setReportsOpen(true);
      if (canStats) navigate("/dashboard/statistiche");
    }
  };

  const staffPerms = Array.isArray(user?.permissions) ? user.permissions : [];
  if (user?.role === "client_staff") {
    if (!staffCanOpenDashboardPath(location.pathname, staffPerms)) {
      return <Navigate to={staffHomePath(user)} replace />;
    }
  }

  const visibleDaily = Array.isArray(dailyItems) ? dailyItems.filter(vis) : [];
  const visibleReports = Array.isArray(reportItems) ? reportItems.filter(vis) : [];
  const visibleSettings = Array.isArray(settingsItems) ? settingsItems.filter(vis) : [];
  const reportsVisible = statsGroupVisible || visibleReports.length > 0;

  return (
    <div className="min-h-screen flex bg-[#F7F7F8]">
      <aside className="w-[17.5rem] bg-white border-r border-[#E4E4E7] flex flex-col">
        <div className="h-16 border-b border-[#E4E4E7] px-4 flex items-center">
          <Link to="/dashboard" className="flex items-center gap-2.5 min-w-0" data-testid="dash-logo-link">
            <img src="/velta-one-mark.png" alt="Velta One" className="w-12 h-7 object-contain shrink-0" />
            <div className="leading-tight min-w-0">
              <div className="font-heading font-semibold tracking-tight text-base text-[#18181B]">Velta One</div>
            </div>
          </Link>
        </div>
        <nav className="flex-1 p-3 overflow-auto pos-scroll">
          {show("cassa") && !dedicatedLodging && hasPerm("cassa") && hasFeature("pos") && (isOwner || isVeltaApk()) && (
            <Link
              to="/cassa"
              data-testid="dash-open-pos-btn"
              className="mb-2 flex items-center justify-center gap-2 h-14 bg-[#B73C27] hover:bg-[#98301F] text-white rounded-xl text-sm font-medium btn-tactile"
              style={{ boxShadow: "0 8px 18px rgba(183,60,39,0.22)" }}
            >
              <ScanLine className="w-5 h-5" /> Apri Cassa
            </Link>
          )}
          {show("palmare") && hasPerm("palmare") && hasFeature("palmare") && (
            <Link
              to="/palmare"
              data-testid="dash-open-palmare-btn"
              className="mb-3 flex items-center justify-center gap-2 h-11 bg-white border border-[#E4E4E7] text-[#18181B] hover:border-[#18181B] rounded-xl text-sm font-medium btn-tactile"
            >
              <Smartphone className="w-5 h-5" /> Palmare
            </Link>
          )}

          <div data-testid="dash-section-vendita" className="px-3 mb-1.5 mt-1 text-[10px] font-medium uppercase tracking-[0.2em] text-[#A1A1AA]">Oggi</div>
          <div className="space-y-0.5">
            {show("home") && <Item to="/dashboard" icon={LayoutDashboard} testid="dash-nav-home" perm="gestionale">Panoramica</Item>}
            {(Array.isArray(visibleDaily) ? visibleDaily : []).map((it) => (
              <Item key={it.key} to={it.to} icon={it.icon} testid={it.testid} perm={it.perm} feature={it.feature}>{it.label}</Item>
            ))}
          </div>

          <div data-testid="dash-section-magazzino" className="sr-only">Magazzino</div>

          {reportsVisible && (
            <div className="mt-4">
              <button
                type="button"
                data-testid="dash-nav-reports-toggle"
                onClick={() => setReportsOpen((v) => !v)}
                className="w-full flex items-center gap-3 px-3 h-11 rounded-xl text-sm font-medium text-[#3F3F46] hover:bg-[#F4F4F5]"
              >
                <BarChart3 className="w-5 h-5" strokeWidth={1.8} />
                Report
                <ChevronDown className={`w-4 h-4 ml-auto text-[#A1A1AA] transition-transform ${reportsOpen ? "rotate-180" : ""}`} />
              </button>
              <div className={`mt-0.5 ml-2 pl-2 border-l border-[#E4E4E7] space-y-0.5 ${reportsOpen ? "" : "hidden"}`}>
                  {statsGroupVisible && (
                    <div>
                      <button
                        data-testid="dash-nav-stats"
                        onClick={toggleStats}
                        className={`w-full flex items-center gap-2.5 px-3 h-10 rounded-lg text-[13px] font-medium transition-colors ${
                          location.pathname === "/dashboard/statistiche" ? "bg-[#18181B] text-white" : "text-[#71717A] hover:text-[#18181B] hover:bg-[#F4F4F5]"
                        }`}
                      >
                        <BarChart3 className="w-4 h-4" strokeWidth={1.8} />
                        Statistiche
                        <ChevronDown className={`w-3.5 h-3.5 ml-auto transition-transform ${statsOpen ? "rotate-180" : ""}`} />
                      </button>
                      <div data-testid="stats-submenu" className={`mt-0.5 ml-3 space-y-0.5 ${statsOpen ? "" : "hidden"}`}>
                        {canStats && <SubItem to="/dashboard/vendite" icon={PieChart} testid="dash-subnav-sales">Vendite</SubItem>}
                        {canDocs && <SubItem to="/dashboard/documenti" icon={ReceiptText} testid="dash-subnav-documents">Documenti</SubItem>}
                        {canGest && <SubItem to="/dashboard/movimenti-gestionali" icon={ShieldAlert} testid="dash-subnav-gestionale">Gestionale</SubItem>}
                      </div>
                    </div>
                  )}
                  {(Array.isArray(visibleReports) ? visibleReports : []).map((it) => (
                    <Item key={it.key} to={it.to} icon={it.icon} testid={it.testid} perm={it.perm} feature={it.feature}>{it.label}</Item>
                  ))}
              </div>
            </div>
          )}

          {visibleSettings.length > 0 && (
            <div className="mt-2" data-testid="dash-section-amministrazione">
              <button
                type="button"
                data-testid="dash-nav-settings"
                onClick={() => setSettingsOpen((v) => !v)}
                className="w-full flex items-center gap-3 px-3 h-11 rounded-xl text-sm font-medium text-[#3F3F46] hover:bg-[#F4F4F5]"
              >
                <Settings2 className="w-5 h-5" strokeWidth={1.8} />
                Impostazioni
                <ChevronDown className={`w-4 h-4 ml-auto text-[#A1A1AA] transition-transform ${settingsOpen ? "rotate-180" : ""}`} />
              </button>
              <div className={`mt-0.5 ml-2 pl-2 border-l border-[#E4E4E7] space-y-0.5 ${settingsOpen ? "" : "hidden"}`}>
                {(Array.isArray(visibleSettings) ? visibleSettings : []).map((it) => (
                  <Item key={it.key} to={it.to} icon={it.icon} testid={it.testid} perm={it.perm} feature={it.feature}>{it.label}</Item>
                ))}
              </div>
            </div>
          )}
        </nav>
        <div className="p-3 border-t border-[#E4E4E7]">
          <div className="px-3 py-2 text-xs">
            <div className="font-medium text-[#18181B] truncate">{user?.name}</div>
            <div className="text-[#A1A1AA] truncate">{user?.email}</div>
          </div>
          <CheckUpdateButton variant="sidebar" />
          {show("messages") && <Item to="/dashboard/messaggi" icon={Mail} testid="dash-nav-messages" perm="gestionale" badge={unreadMsgs}>Messaggi</Item>}
          <button data-testid="dash-logout-btn" onClick={user?.impersonation ? exitImpersonation : logout} className="w-full flex items-center gap-3 px-3 h-11 rounded-xl text-sm font-medium text-[#3F3F46] hover:bg-[#F4F4F5] transition-colors">
            <LogOut className="w-5 h-5" /> Esci
          </button>
        </div>
      </aside>
      <main className="flex-1 min-w-0 overflow-auto">
        <div className="h-12 border-b border-[#E4E4E7] bg-white px-4 flex items-center justify-end">
          <StoreSwitcher compact />
        </div>
        <ErrorBoundary>
          <Outlet />
        </ErrorBoundary>
      </main>
    </div>
  );
}
